import { changeActiveModalNode } from "../reducers/node-modal";
import { addNode, changeNode, findNode } from "../reducers/nodes";
import {
  DivColNode,
  DivRowNode,
  FormNode,
  NodeType,
  PageNode,
} from "../types/PageNode";
import { BackClass } from "../types/BackClass";
import { AnyAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { changeNewNode } from "../reducers/app";
import { UserQuery } from "./User/UserService.hooks";
import { RequestPageType } from "../types/RequestPageType";
import { LIMIT_FREE } from "../types/subscription";
import { OnClickActionType } from "../types/OnClickAction";
import { canInsertNodeWithoutDuplicateHeader, containsHeaderNode } from "../utils/nodes/headerNode";

let iteration: number = 0;

/**
 * It's need for template nodes. Because they have similar ids on second time
 * @deprecated
 * @param node
 * @param parentId
 * @param idArray
 */
export function addNewIdForChildNodes(
  node: PageNode,
  parentId: string = null,
  idArray: { [key: string]: string } = null,
): { [key: string]: string } {
  iteration++;
  if (idArray === null) {
    idArray = {};
  }
  const newID = Date.now() + "-" + iteration;
  idArray[node.id] = newID;

  node.id = newID;
  if (node.type === NodeType.FormNode) {
    const formNode = node as FormNode;
    formNode.formSettings = {
      ...formNode.formSettings,
      uniqueKey: `${node.id}-form`,
    };
  }
  if (parentId != null) node.parentId = parentId;
  if (node.nodes.length > 0) {
    node.nodes.forEach((foreachNode) => {
      addNewIdForChildNodes(foreachNode, node.id, idArray);
    });
  }
  return idArray;
}

export function getNodeWithNewIds(
  node: PageNode,
  parentId: string = null,
): PageNode {
  iteration++;
  let copyNode: PageNode = { ...node };
  copyNode.id = Date.now() + "-" + iteration;
  if (copyNode.type === NodeType.FormNode) {
    const formNode = copyNode as FormNode;
    formNode.formSettings = {
      ...formNode.formSettings,
      uniqueKey: `${copyNode.id}-form`,
    };
  }
  if (parentId != null) copyNode.parentId = parentId;
  if (copyNode.nodes.length > 0) {
    let childNodes: PageNode[] = [];
    copyNode.nodes.forEach((foreachNode) => {
      childNodes.push(getNodeWithNewIds(foreachNode, copyNode.id));
    });
    copyNode.nodes = childNodes;
  }
  return copyNode;
}

export function changeDataBSTargetId(
  node: PageNode,
  idArray: { [key: string]: string },
) {
  if (node.attributes && node.attributes["data-bs-target"]) {
    const newID =
      idArray[node.attributes["data-bs-target"].replace("#blockContent", "")];
    node.attributes = {
      ...node.attributes,
      "data-bs-target": `#blockContent${newID}`,
    };
  }
  if (node.nodes.length > 0) {
    node.nodes.forEach((foreachNode) => {
      changeDataBSTargetId(foreachNode, idArray);
    });
  }
}

export function getDefaultDivColNode(
  id: string,
  tagName: string,
  className: string,
  parentId: string,
  type: string,
): PageNode {
  return {
    id: id,
    type: type,
    tagName: tagName,
    className: className,
    style: [] as object,
    classes: [] as string[],
    nodes: [] as PageNode[],
    parentId: parentId,
    backClassesIndexed: [] as unknown as BackClass,
    backClassesMobile: [] as unknown as BackClass,
  };
}

export function addNodeToActiveNodeWithAutoSelect(
  dispatch: Dispatch<AnyAction>,
  activeNode: PageNode,
  nodeToAdd: PageNode,
) {
  dispatch(changeNewNode(nodeToAdd));
  dispatch(
    changeActiveModalNode(addNodeToActiveNode(dispatch, activeNode, nodeToAdd)),
  );
}

export function addNodeToActiveNode(
  dispatch: Dispatch<AnyAction>,
  activeNode: PageNode,
  nodeToAdd: PageNode,
): PageNode {
  let newNode = { ...nodeToAdd };
  newNode.style = {};
  newNode.classes ??= [];
  newNode.nodes ??= [];
  dispatch(addNode({ activeNode, newNode }));
  return newNode;
}

export function addNodeAfterActiveNode(
  dispatch: Dispatch<AnyAction>,
  activeNode: PageNode,
  nodeToAdd: PageNode,
  nodes: ParentNode[],
): PageNode {
  let newNode = { ...nodeToAdd };
  if (
    !canInsertNodeWithoutDuplicateHeader(
      nodes[0] as unknown as PageNode,
      newNode,
    )
  ) {
    return newNode;
  }
  const parentNode = findNode(nodes[0], newNode.parentId);
  let newParentNode = {
    ...parentNode,
    nodes: [...parentNode.nodes],
  };
  const index = containsHeaderNode(newNode)
    ? 0
    : newParentNode.nodes.findIndex(
        (node: PageNode) => node.id === activeNode.id,
      ) + 1;
  newParentNode.nodes.splice(index, 0, newNode);
  dispatch(changeNode({ editedNode: newParentNode }));
  return newNode;
}

export function makeStructureChange(
  previousNodeState: DivRowNode,
  newNodeState: DivRowNode,
  pageType: RequestPageType.CommonPage | RequestPageType.MobilePage,
): DivRowNode {
  const newNodeStateCopy: DivRowNode = { ...newNodeState };
  let nodes = [...newNodeStateCopy.nodes];

  const structureKeys: {
    structureKey: keyof DivRowNode;
    colSizeKey: keyof DivColNode;
  }[] =
    pageType === RequestPageType.MobilePage
      ? [{ structureKey: "structure", colSizeKey: "colSize" }]
      : [
          { structureKey: "structure", colSizeKey: "colSize" },
          { structureKey: "mobileStructure", colSizeKey: "colSizeMobile" },
        ];

  const nodesCount = newNodeState.nodes.length;
  const newNodesCount =
    pageType === RequestPageType.CommonPage
      ? newNodeState.structure > newNodeState.mobileStructure
        ? newNodeState.structure
        : newNodeState.mobileStructure
      : newNodeState.structure;

  if (nodesCount !== newNodesCount) {
    const diff = Math.abs(nodesCount - newNodesCount);
    let lastId = Date.now();

    for (let i = 0; i < diff; i++) {
      const newNode: PageNode = getDefaultDivColNode(
        (lastId++).toString(),
        "div",
        "block",
        newNodeStateCopy.id.toString(),
        NodeType.DivColNode,
      );
      nodes = [...nodes, newNode];
    }
  }

  structureKeys.forEach(({ structureKey, colSizeKey }) => {
    const currentStructure = newNodeStateCopy[structureKey] ?? 0;
    const previousStructure = previousNodeState[structureKey] ?? 0;

    if (!currentStructure) return;

    const newColSize = Math.round(12 / currentStructure);

    if (currentStructure < previousStructure) {
      const otherStructureKey =
        structureKey === "structure" ? "mobileStructure" : "structure";
      const otherStructure = newNodeStateCopy[otherStructureKey] ?? 0;

      const minCols = Math.max(currentStructure, otherStructure);

      while (nodes.length > minCols) {
        const popElement = nodes.pop();
        if (popElement && nodes.length > 0) {
          const lastChild = { ...nodes[nodes.length - 1] };
          lastChild.nodes = [...lastChild.nodes, ...popElement.nodes];
          nodes[nodes.length - 1] = lastChild;
        }
      }
    }

    nodes = nodes.map((node: DivColNode) => {
      if (
        node[colSizeKey] === undefined ||
        currentStructure !== previousStructure
      ) {
        const updatedNode = { ...node };
        if (pageType === RequestPageType.CommonPage) {
          (updatedNode as any)[colSizeKey] = newColSize;
        } else if (pageType === RequestPageType.MobilePage) {
          (updatedNode as any)[colSizeKey] = newColSize;
        }
        return updatedNode;
      }
      return node;
    });
  });

  newNodeStateCopy.nodes = nodes;

  return newNodeStateCopy;
}

export function getNewNode<T extends PageNode = PageNode>(
  type: NodeType,
  parentNode?: PageNode,
  override?: Partial<T>,
) {
  let newNode: PageNode = {
    classes: [],
    style: [],
    id: Date.now().toString(),
    nodes: [],
    parentId: parentNode ? parentNode.id : undefined,
    type: type,
  };
  if (override) {
    newNode = { ...newNode, ...override };
  }

  return newNode as T;
}

export function getAllNodes(startNode: PageNode, nodes?: PageNode[]) {
  if (nodes === undefined) {
    nodes = [];
  }
  nodes.push(startNode);
  if (startNode.nodes.length > 0) {
    startNode.nodes.map((node) => {
      getAllNodes(node, nodes);
    });
  }
  return nodes;
}

export const isFormLimitReached = (
  nodes: PageNode[],
  nodeToAdd: PageNode,
  user: UserQuery,
) => {
  const isPremiumUser = user?.user?.activePremium || user?.user?.admin;

  if (isPremiumUser || !nodes[0]) {
    return false;
  }

  const existingFormsCount = getAllNodes(nodes[0]).filter(
    (node) => node.type === NodeType.FormNode,
  ).length;
  const addedFormsCount = getAllNodes(nodeToAdd).filter(
    (node) => node.type === NodeType.FormNode,
  ).length;

  return existingFormsCount + addedFormsCount > LIMIT_FREE.FORMS;
};

export function findTopParent(
  nodeId: string,
  nodes: PageNode[],
): PageNode | null {
  const nodeMap: Record<string, PageNode> = {};
  function buildNodeMap(nodes: PageNode[]): void {
    for (const node of nodes) {
      nodeMap[node.id] = node;
      if (node.nodes) {
        buildNodeMap(node.nodes);
      }
    }
  }

  buildNodeMap(nodes);

  let currentNode = nodeMap[nodeId];
  while (currentNode && currentNode.parentId !== "1") {
    currentNode = nodeMap[currentNode.parentId] || null;
  }

  return currentNode;
}

export const checkOnBlockLimit = (nodes: PageNode[], user: UserQuery) => {
  const isPremium = user?.user.activePremium;
  const isAdmin = user?.user.admin;
  let blocks = nodes[0].nodes.length;

  if (
    blocks >= LIMIT_FREE.BLOCKS &&
    window.self === window.top &&
    !isAdmin &&
    !isPremium
  ) {
    return true;
  }
  return false;
};

const isEmptyVcard = async (vcard: string) => {
  const response = await fetch(vcard);
  const text = await response.text();

  const hasName = !text.includes("N;CHARSET=UTF-8:;;;;");
  const hasOrg = /ORG[;:].*:\s*\S+/.test(text);
  const hasPhone = /TEL[;:].*:\s*\d+/.test(text);
  const hasEmail = /EMAIL[;:].*:\s*\S+@\S+/.test(text);
  const hasPhoto = text.includes("PHOTO;");
  const hasExtra =
    /ADR|BDAY|TITLE|URL/.test(text) &&
    text.match(/(ADR|BDAY|TITLE|URL)[;:].*:\s*\S+/);

  const isEmpty = !(
    hasName ||
    hasOrg ||
    hasPhone ||
    hasEmail ||
    hasPhoto ||
    hasExtra
  );

  return isEmpty;
};

export const checkDefaultLinks = async (nodes: PageNode) => {
  const defaultLinks = [
    "https://me-page.com/",
    "http://me-page.com/",
    "https://me-page.com",
    "http://me-page.com",
  ];

  const nodesIdsWithWrongAction: string[] = [];

  const traverse = async (node: PageNode) => {
    const { onClickAction, type, id, nodes: children } = node;
    const actionValue = onClickAction?.value;
    const actionType = onClickAction?.type;

    const isInvalidLink =
      actionType === OnClickActionType.OpenLink &&
      (!actionValue || defaultLinks.includes(actionValue));

    const isButtonWithoutAction =
      type === NodeType.ButtonNode &&
      (actionType === OnClickActionType.None || !onClickAction);

    const isVcardWithoutData =
      actionType === OnClickActionType.SaveVcard &&
      (await isEmptyVcard(actionValue));

    if (isInvalidLink || isButtonWithoutAction || isVcardWithoutData) {
      nodesIdsWithWrongAction.push(id);
    }

    if (children && children.length > 0) {
      for (const child of children) {
        await traverse(child);
      }
    }
  };

  await traverse(nodes);
  return nodesIdsWithWrongAction;
};
